import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserProfile from '../auth/UserProfile';
import LogoutButton from '../auth/LogoutButton';
import { FaCog, FaUsers, FaChalkboardTeacher, FaGraduationCap, FaChartBar, FaChevronDown } from 'react-icons/fa';
import logo from '../../assets/images/logo_light.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const adminDropdownRef = useRef(null);

  // Check if user is admin (case-insensitive)
  const isAdmin = () => {
    return userProfile?.role?.toLowerCase() === 'admin';
  };

  // Check if user is parent (case-insensitive)
  const isParent = () => {
    return userProfile?.role?.toLowerCase() === 'parent';
  };

  // Get dashboard path based on user role
  const getDashboardPath = () => {
    if (isAdmin()) return '/admin';
    if (isParent()) return '/parent/dashboard';
    return '/dashboard';
  };

  // Close admin dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target)) {
        setIsAdminDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navLinkClass = (path) => {
    return `text-gray-700 hover-primary transition-colors duration-200 ${isActive(path) ? 'text-primary font-semibold' : ''
      }`;
  };

  const mobileNavLinkClass = (path) => {
    return `block px-3 py-2 text-gray-700 hover-primary transition-colors duration-200 ${isActive(path) ? 'text-primary font-semibold bg-gray-50' : ''
      }`;
  };

  const toggleAdminDropdown = () => {
    setIsAdminDropdownOpen(!isAdminDropdownOpen);
  };

  return (
    <header className="bg-white shadow-md fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/">
              <img src={logo} alt="NexGen Learning Logo" className="h-12 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {currentUser && !isAdmin() ? (
              // Logged-in user navigation
              <>
                <Link to={getDashboardPath()} className={navLinkClass(getDashboardPath())}>Dashboard</Link>
                {!isParent() && (
                  <>
                    <Link to="/teachers" className={navLinkClass('/teachers')}>Teachers</Link>
                    <Link to="/profiles" className={navLinkClass('/profiles')}>Profiles</Link>
                  </>
                )}
                {isParent() && (
                  <Link to="/profiles" className={navLinkClass('/profiles')}>Children</Link>
                )}
                <Link to="/account" className={navLinkClass('/account')}>Account</Link>
              </>
            ) : (
              // Public navigation
              <>
                <Link to="/features" className={navLinkClass('/features')}>Features</Link>
                <Link to="/solutions" className={navLinkClass('/solutions')}>Solutions</Link>
                <Link to="/pricing" className={navLinkClass('/pricing')}>Pricing</Link>
                <Link to="/resources" className={navLinkClass('/resources')}>Resources</Link>
                <Link to="/contact" className={navLinkClass('/contact')}>Contact</Link>
              </>
            )}
          </nav>

          {/* Action Buttons - Show different content based on auth status */}
          <div className="hidden md:flex items-center space-x-4">
            {currentUser ? (
              // User is signed in - show user profile or admin dropdown
              isAdmin() ? (
                <div className="relative" ref={adminDropdownRef}>
                  <button
                    onClick={toggleAdminDropdown}
                    className="h-8 w-8 rounded-full bg-primary flex items-center justify-center hover:bg-primary-dark transition-colors"
                  >
                    <span className="text-white text-sm font-medium">
                      {(userProfile?.firstName || currentUser.displayName || currentUser.email).charAt(0).toUpperCase()}
                    </span>
                  </button>

                  {/* Admin Dropdown Menu */}
                  {isAdminDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        {/* User Info Section */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">
                            {userProfile?.firstName && userProfile?.lastName 
                              ? `${userProfile.firstName} ${userProfile.lastName}`
                              : currentUser.displayName || currentUser.email
                            }
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {currentUser.email}
                          </p>
                          <p className="text-xs text-blue-600 font-medium mt-1">
                            {userProfile?.role?.charAt(0).toUpperCase() + userProfile?.role?.slice(1) || 'Admin'}
                          </p>
                        </div>

                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Admin Panel
                        </div>
                        
                        <Link
                          to="/admin"
                          onClick={() => setIsAdminDropdownOpen(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FaCog className="mr-3 h-4 w-4 text-gray-400" />
                          Dashboard
                        </Link>
                        
                        <Link
                          to="/admin?tab=users"
                          onClick={() => setIsAdminDropdownOpen(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FaUsers className="mr-3 h-4 w-4 text-gray-400" />
                          User Management
                        </Link>
                        
                        <Link
                          to="/admin?tab=teachers"
                          onClick={() => setIsAdminDropdownOpen(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FaChalkboardTeacher className="mr-3 h-4 w-4 text-gray-400" />
                          Teacher Management
                        </Link>
                        
                        <Link
                          to="/admin?tab=sessions"
                          onClick={() => setIsAdminDropdownOpen(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FaGraduationCap className="mr-3 h-4 w-4 text-gray-400" />
                          Learning Sessions
                        </Link>
                        
                        <Link
                          to="/admin?tab=analytics"
                          onClick={() => setIsAdminDropdownOpen(false)}
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FaChartBar className="mr-3 h-4 w-4 text-gray-400" />
                          Analytics
                        </Link>

                        {/* Sign Out Section */}
                        <div className="border-t border-gray-100 mt-2 pt-2">
                          <LogoutButton 
                            onClick={() => setIsAdminDropdownOpen(false)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            Sign Out
                          </LogoutButton>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <UserProfile />
              )
            ) : (
              // User is not signed in - show login/register buttons
              <>
                <Link to="/auth/register" className="btn-outline-primary">
                  Start Free Trial
                </Link>
                <Link to="/auth/login" className="btn-primary">
                  Login
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex justify-center align-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-primary mx-4"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Mobile action button */}
            {currentUser ? (
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center mr-2">
                  <span className="text-white text-sm font-medium">
                    {(userProfile?.firstName || currentUser.displayName || currentUser.email).charAt(0).toUpperCase()}
                  </span>
                </div>
                {isAdmin() && (
                  <span className="text-xs text-blue-600 font-medium">Admin</span>
                )}
              </div>
            ) : (
              <Link to="/auth/login" className="btn-primary">
                Login
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {currentUser && !isAdmin() ? (
                // Logged-in user mobile navigation
                <>
                  <Link to={getDashboardPath()} className={mobileNavLinkClass(getDashboardPath())} onClick={() => setIsMenuOpen(false)}>Dashboard</Link>
                  {!isParent() && (
                    <>
                      <Link to="/teachers" className={mobileNavLinkClass('/teachers')} onClick={() => setIsMenuOpen(false)}>Teachers</Link>
                      <Link to="/profiles" className={mobileNavLinkClass('/profiles')} onClick={() => setIsMenuOpen(false)}>Profiles</Link>
                    </>
                  )}
                  {isParent() && (
                    <Link to="/profiles" className={mobileNavLinkClass('/profiles')} onClick={() => setIsMenuOpen(false)}>Children</Link>
                  )}
                  <Link to="/account" className={mobileNavLinkClass('/account')} onClick={() => setIsMenuOpen(false)}>Account</Link>
                </>
              ) : (
                // Public mobile navigation
                <>
                  <Link to="/features" className={mobileNavLinkClass('/features')} onClick={() => setIsMenuOpen(false)}>Features</Link>
                  <Link to="/solutions" className={mobileNavLinkClass('/solutions')} onClick={() => setIsMenuOpen(false)}>Solutions</Link>
                  <Link to="/pricing" className={mobileNavLinkClass('/pricing')} onClick={() => setIsMenuOpen(false)}>Pricing</Link>
                  <Link to="/resources" className={mobileNavLinkClass('/resources')} onClick={() => setIsMenuOpen(false)}>Resources</Link>
                  <Link to="/contact" className={mobileNavLinkClass('/contact')} onClick={() => setIsMenuOpen(false)}>Contact</Link>
                </>
              )}
              
              {/* Mobile auth section */}
              {currentUser ? (
                <div className="pt-4 border-t border-gray-200">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">
                      {userProfile?.firstName && userProfile?.lastName 
                        ? `${userProfile.firstName} ${userProfile.lastName}`
                        : currentUser.displayName || currentUser.email
                      }
                    </p>
                    <p className="text-xs text-gray-500">
                      {currentUser.email}
                    </p>
                    {isAdmin() && (
                      <p className="text-xs text-blue-600 font-medium">
                        Admin
                      </p>
                    )}
                  </div>
                  
                  {isAdmin() && (
                    <div className="px-3 py-2 space-y-1">
                      <Link 
                        to="/admin" 
                        className={mobileNavLinkClass('/admin')}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                      <Link 
                        to="/admin?tab=users" 
                        className={mobileNavLinkClass('/admin')}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        User Management
                      </Link>
                      <Link 
                        to="/admin?tab=teachers" 
                        className={mobileNavLinkClass('/admin')}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Teacher Management
                      </Link>
                      <Link 
                        to="/admin?tab=sessions" 
                        className={mobileNavLinkClass('/admin')}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Learning Sessions
                      </Link>
                      <Link 
                        to="/admin?tab=analytics" 
                        className={mobileNavLinkClass('/admin')}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Analytics
                      </Link>
                    </div>
                  )}
                  
                  <LogoutButton className="block w-full text-left px-3 py-2 text-gray-700 hover:text-primary transition-colors">
                    Sign Out
                  </LogoutButton>
                </div>
              ) : (
                <div className="pt-4 border-t border-gray-200 space-y-1">
                  <Link to="/auth/register" className={mobileNavLinkClass('/auth/register')}>
                    Start Free Trial
                  </Link>
                  <Link to="/auth/login" className={mobileNavLinkClass('/auth/login')}>
                    Login
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;